import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getSitePost, updateSitePost } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
// import TiptapEditor from "@/components/ui/TiptapEditor";
import SimpleEditor from "@/components/ui/SimpleEditor";
import { toast } from "sonner";
import { Eye, Save, Upload } from "lucide-react";

export default function SitePostEdit() {
  const { siteSlug, postSlug } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: post,
    isLoading,
    isError,
    error: queryError,
  } = useQuery({
    queryKey: ["site-post", siteSlug, postSlug],
    queryFn: () => getSitePost(siteSlug as string, postSlug as string),
    enabled: !!siteSlug && !!postSlug,
  });

  const [formTitle, setFormTitle] = useState("");
  const [formExcerpt, setFormExcerpt] = useState("");
  const [formBody, setFormBody] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (post) {
      setFormTitle(post.title);
      setFormExcerpt(post.excerpt || "");
      setFormBody(post.post_body);
    }
  }, [post]);

  const updateMutation = useMutation({
    mutationFn: (data: { title: string; excerpt: string; body: string }) =>
      updateSitePost(siteSlug as string, postSlug as string, data),
    onSuccess: () => {
      toast.success("Post updated");
      queryClient.invalidateQueries({ queryKey: ["site-posts", siteSlug] });
      //navigate(`/sites/${siteSlug}/posts`);
    },
    onError: (err: any) => {
      setFormError(err.message || "Failed to update post");
    },
  });

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto mt-8">
        <Card className="p-8">
          <div className="text-lg font-semibold mb-2">Loading...</div>
        </Card>
      </div>
    );
  }

  if (isError || !post) {
    return (
      <div className="max-w-2xl mx-auto mt-8">
        <Card className="p-8">
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {(queryError as Error)?.message || "Failed to load post"}
            </AlertDescription>
          </Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row gap-8 mt-8">
      {/* Main editor area */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <button
            className="text-sm text-muted-foreground hover:underline flex items-center"
            onClick={() => navigate(-1)}
          >
            &larr; Back
          </button>
          <span className="text-muted-foreground">/</span>
          <span className="text-muted-foreground">{post.title}</span>
        </div>
        <Card className="bg-default p-8">
          <div className="flex flex-col gap-4">
            <Input
              type="text"
              placeholder="Title"
              value={formTitle}
              onChange={e => setFormTitle(e.target.value)}
              className="text-3xl font-bold bg-transparent border-none focus:ring-0 focus-visible:ring-0 px-0"
              autoFocus
            />
            {/* Excerpt input moved to About card in sidebar */}
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">
                Article body
              </label>
              {/*<TiptapEditor
                value={formBody}
                onChange={setFormBody}
                placeholder="Write your article..."
                className="bg-muted border border-muted rounded-b"
              />*/}
              <SimpleEditor
                value={formBody}
                onChange={setFormBody}
                scope="post"
              />
            </div>
            {formError && (
              <Alert variant="destructive" className="mb-2">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
            <div className="flex gap-2 mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={updateMutation.status === "pending"}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => toast.info("Preview not implemented")}
                className="flex items-center gap-2"
              >
                <Eye className="w-4 h-4" /> Preview
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => updateMutation.mutate({ title: formTitle, excerpt: formExcerpt, body: formBody })}
                disabled={updateMutation.status === "pending" || !formTitle || !formBody}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" /> Save draft
              </Button>
              <Button
                type="button"
                onClick={() => updateMutation.mutate({ title: formTitle, excerpt: formExcerpt, body: formBody })}
                disabled={updateMutation.status === "pending" || !formTitle || !formBody}
                className="flex items-center gap-2"
              >
                <Upload className="w-4 h-4" /> Publish
              </Button>
            </div>
          </div>
        </Card>
      </div>
      {/* Sidebar */}
      <div className="w-full md:w-80 flex-shrink-0">
        <Card className="bg-default border-subtle p-6 mb-4">
          <div className="flex items-center gap-3 mb-4">
            <img
              src={post.author_avatar_url || "https://www.gravatar.com/avatar?d=mp"}
              alt={post.author_name || "Author"}
              className="w-12 h-12 rounded-full object-cover border"
            />
            <div>
              <div className="font-semibold">{post.author_name || "Author Name"}</div>
              <div className="text-xs text-muted-foreground">{post.author_bio || "Author bio goes here."}</div>
            </div>
          </div>
          <div className="flex gap-4 text-xs text-muted-foreground mb-2">
            <div>
              <div className="font-bold text-lg">{post.views || 0}</div>
              <div>Views</div>
            </div>
            <div>
              <div className="font-bold text-lg">{post.bookmarks || 0}</div>
              <div>Bookmarks</div>
            </div>
          </div>
          <Button size="sm" variant="outline" className="w-full mt-2">
            Copy link
          </Button>
        </Card>
        <Card className="bg-default border-subtle p-6">
          <div className="font-semibold mb-2">About</div>
          <div className="mb-2 text-sm text-muted-foreground">
            {post.about || "This is a sample about section for the author or the article. You can customize this sidebar as needed."}
          </div>
          <div className="mb-2">
            <label className="block text-xs font-medium text-muted-foreground mb-1" htmlFor="excerpt">
              Excerpt
            </label>
            <textarea
              id="excerpt"
              value={formExcerpt}
              onChange={e => setFormExcerpt(e.target.value)}
              className="w-full min-h-[80px] rounded border border-subtle bg-muted px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Enter a short excerpt for this post"
            />
          </div>
        </Card>
      </div>
    </div>
  );
}
